import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const SalonDayOffsSettings = () => {
  const { shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [shopDayOffs, setShopDayOffs] = useState([]);
  const [showAddShopDayOff, setShowAddShopDayOff] = useState(false);
  const [newShopDayOff, setNewShopDayOff] = useState({
    date: '',
    reason: ''
  });

  const loadShopDayOffs = useCallback(async () => {
    try {
      if (!shopId) {
        toast.error(t('admin.settings_day_off_error_load'));
        return;
      }
      const response = await axiosClient.get(`/admin/shops/${shopId}/day-offs`);
      setShopDayOffs(response.data);
    } catch (error) {
      console.error('Error loading salon day-offs:', error);
      toast.error(t('admin.settings_day_off_error_load'));
    }
  }, [shopId, t]);

  const addShopDayOff = async () => {
    try {
      if (!shopId) {
        toast.error(t('admin.settings_day_off_error_add'));
        return;
      }
      await axiosClient.post(`/admin/shops/${shopId}/day-offs`, newShopDayOff);
      toast.success(t('admin.settings_day_off_added'));
      setShowAddShopDayOff(false);
      setNewShopDayOff({
        date: '',
        reason: ''
      });
      loadShopDayOffs();
    } catch (error) {
      console.error('Error adding salon day-off:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_day_off_error_add');
      toast.error(errorMessage);
    }
  };

  const deleteShopDayOff = async (dayOffId) => {
    try {
      if (!shopId) {
        toast.error(t('admin.settings_day_off_error_delete'));
        return;
      }
      await axiosClient.delete(`/admin/shops/${shopId}/day-offs/${dayOffId}`);
      toast.success(t('admin.settings_day_off_deleted'));
      loadShopDayOffs();
    } catch (error) {
      console.error('Error deleting salon day-off:', error);
      toast.error(t('admin.settings_day_off_error_delete'));
    }
  };

  useEffect(() => {
    loadShopDayOffs();
  }, [loadShopDayOffs]);

  return (
    <Card>
      <CardHeader
        title={t('admin.settings_day_offs_title')}
        subtitle={t('admin.settings_day_offs_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        {/* Salon Day-Offs List */}
        <div className="space-y-3 mb-4">
          {shopDayOffs.map(dayOff => (
            <div
              key={dayOff.id}
              className="p-3 sm:p-4 rounded-xl bg-white/30 dark:bg-zinc-800/30 backdrop-blur-sm border border-white/20 dark:border-white/10"
            >
              {/* Mobile Layout */}
              <div className="sm:hidden space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                    {dayOff.date}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => deleteShopDayOff(dayOff.id)}
                    aria-label={t('admin.delete')}
                    className="px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                  >
                    {t('admin.delete')}
                  </motion.button>
                </div>
                {dayOff.reason && (
                  <div className="text-sm italic text-zinc-500 dark:text-zinc-400">
                    {dayOff.reason}
                  </div>
                )}
              </div>
            
              {/* Desktop Layout */}
              <div className="hidden sm:flex sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{dayOff.date}</span>
                  {dayOff.reason && (
                    <span className="ml-2 text-sm italic text-zinc-500 dark:text-zinc-400 truncate">
                      ({dayOff.reason})
                    </span>
                  )}
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => deleteShopDayOff(dayOff.id)}
                  aria-label={t('admin.delete')}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                >
                  {t('admin.delete')}
                </motion.button>
              </div>
          </div>
        ))}
        
          {shopDayOffs.length === 0 && (
            <p className="text-sm italic text-zinc-500 dark:text-zinc-400">
              {t('admin.settings_no_day_offs')}
            </p>
          )}
      </div>

      {/* Add Salon Day-Off Modal */}
      {showAddShopDayOff && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl p-4 w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
          >
            <div className="flex items-center mb-4">
              <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center mr-2" style={{ '--accent': ACCENT }}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                  {t('admin.settings_add_day_off_title')}
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {t('admin.settings_add_day_off_subtitle')}
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                  {t('admin.settings_date_label')}
                </label>
                <input
                  type="date"
                  value={newShopDayOff.date}
                  onChange={(e) => setNewShopDayOff(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                  {t('admin.settings_reason_optional')}
                </label>
                <input
                  type="text"
                  value={newShopDayOff.reason}
                  onChange={(e) => setNewShopDayOff(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={t('admin.settings_reason_placeholder')}
                  className="w-full px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddShopDayOff(false)}
                aria-label={t('admin.cancel')}
                className="px-6 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
              >
                {t('admin.cancel')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addShopDayOff}
                disabled={!newShopDayOff.date}
                className="px-6 py-3 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transition-all duration-200"
                style={{ background: newShopDayOff.date ? ACCENT : '#9ca3af' }}
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('admin.settings_add_day_off_short')}
              </motion.button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

        {/* Additional Info */}
        <div className="mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t('admin.settings_day_off_note')}
          </p>
        </div>

        {/* Action Button */}
        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-4 border-t border-white/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddShopDayOff(true)}
            className="w-full sm:w-auto px-6 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200"
            style={{ background: ACCENT }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{t('admin.settings_add_day_off_btn')}</span>
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

export default SalonDayOffsSettings;
